import { Suspense } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "@/components/ErrorBoundary";

interface AppRuntimeBoundaryProps {
  children: ReactNode;
  resetKey: string;
}

const APP_ERROR_TITLE_ID = "app-error-title";
const APP_ERROR_MESSAGE_ID = "app-error-message";
const PAGE_LOADING_LABEL_ID = "page-loading-label";

export function AppRuntimeBoundary({
  children,
  resetKey,
}: AppRuntimeBoundaryProps) {
  return (
    <ErrorBoundary resetKey={resetKey} fallback={<AppErrorFallback />}>
      <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function AppErrorFallback() {
  const { t } = useTranslation();

  return (
    <section
      className="app-error"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      aria-labelledby={APP_ERROR_TITLE_ID}
      aria-describedby={APP_ERROR_MESSAGE_ID}
    >
      <div className="app-error__panel">
        <h1 id={APP_ERROR_TITLE_ID} className="app-error__title">
          {t("app.errorTitle")}
        </h1>
        <p id={APP_ERROR_MESSAGE_ID} className="app-error__message">
          {t("app.errorMessage")}
        </p>
      </div>
    </section>
  );
}

function PageLoadingFallback() {
  const { t } = useTranslation();

  return (
    <div
      className="page-loading"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-labelledby={PAGE_LOADING_LABEL_ID}
    >
      <span id={PAGE_LOADING_LABEL_ID} className="visually-hidden">
        {t("a11y.loading")}
      </span>
      <span className="page-loading__spinner" aria-hidden />
    </div>
  );
}
